// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * VerifiedBadge (#68, trial) — an icon-only verified seal right of the page title:
 * gold for Pilot users, dark blue for Standard users. Display-only, fed by
 * untrusted query-string flags (see the Trust rule in `./verified-badges`).
 *
 *  - Icon: lucide `BadgeCheck` (already a dependency), seal filled with the
 *    tone colour and a white check, 20 px to sit with the `text-nc-4` title.
 *  - Colours: gold `#B8860B` (~3.3:1 on white, enough for non-text UI; no NC
 *    gold token exists, so it is local to this trial, see #68) and navy
 *    `--nc-color-primary-2`.
 *  - Icon only (PM decision, #68): no tooltip, no visible text, not
 *    focusable. `role="img"` with the label as its accessible name keeps the
 *    meaning available to assistive tech.
 */

import { BadgeCheck } from "lucide-react";

import { cn } from "@/lib/utils";

import { VERIFIED_BADGES, type VerifiedBadgeKey, type VerifiedBadgeTone } from "./verified-badges";

const TONE_CLASSES: Readonly<Record<VerifiedBadgeTone, string>> = {
  gold: "text-[#B8860B]",
  navy: "text-[var(--nc-color-primary-2)]",
};

export function VerifiedBadge({ badgeKey }: { badgeKey: VerifiedBadgeKey }) {
  const badge = VERIFIED_BADGES[badgeKey];
  return (
    <span
      role="img"
      aria-label={badge.label}
      data-badge={badgeKey}
      className={cn("inline-flex", TONE_CLASSES[badge.tone])}
    >
      <BadgeCheck className="size-5" fill="currentColor" stroke="white" strokeWidth={2} aria-hidden="true" />
    </span>
  );
}
